import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Shield, CheckCircle, AlertTriangle, XCircle, FileText } from 'lucide-react';

const complianceFrameworks = [
  { id: 'gdpr', name: 'GDPR', status: 'compliant', score: 95 },
  { id: 'soc2', name: 'SOC 2 Type II', status: 'compliant', score: 88 },
  { id: 'hipaa', name: 'HIPAA', status: 'partial', score: 72 },
  { id: 'pci', name: 'PCI DSS', status: 'non-compliant', score: 45 },
];

const complianceChecks = [
  { name: 'Data Encryption at Rest', status: 'passing', category: 'Security' },
  { name: 'Access Logging', status: 'passing', category: 'Audit' },
  { name: 'Data Retention Policies', status: 'warning', category: 'Privacy' },
  { name: 'User Consent Management', status: 'passing', category: 'Privacy' },
  { name: 'Incident Response Plan', status: 'failing', category: 'Security' },
];

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'compliant':
    case 'passing':
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case 'partial':
    case 'warning':
      return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
    case 'non-compliant':
    case 'failing':
      return <XCircle className="h-4 w-4 text-red-500" />;
    default:
      return <Shield className="h-4 w-4 text-gray-500" />;
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'compliant':
    case 'passing':
      return 'bg-green-100 text-green-800';
    case 'partial':
    case 'warning':
      return 'bg-yellow-100 text-yellow-800';
    case 'non-compliant':
    case 'failing':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export default function ComplianceDashboard() {
  const overallScore = Math.round(
    complianceFrameworks.reduce((sum, f) => sum + f.score, 0) / complianceFrameworks.length
  );

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Shield className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-3xl font-bold">Compliance Dashboard</h1>
          <p className="text-muted-foreground">
            Monitor compliance status across frameworks and regulations
          </p>
        </div>
      </div>

      {/* Overall Compliance Score */}
      <Card>
        <CardHeader>
          <CardTitle>Overall Compliance Score</CardTitle>
          <CardDescription>
            Aggregate score across all monitored compliance frameworks
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-4">
            <div className="text-4xl font-bold">{overallScore}%</div>
            <div className="flex-1">
              <Progress value={overallScore} className="h-3" />
            </div>
            <Badge className={getStatusColor(overallScore >= 80 ? 'compliant' : overallScore >= 60 ? 'partial' : 'non-compliant')}>
              {overallScore >= 80 ? 'Good' : overallScore >= 60 ? 'Needs Work' : 'Critical'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Compliance Frameworks */}
      <Card>
        <CardHeader>
          <CardTitle>Compliance Frameworks</CardTitle>
          <CardDescription>
            Status and scores for major compliance frameworks
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {complianceFrameworks.map((framework) => (
              <div key={framework.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center space-x-3">
                  {getStatusIcon(framework.status)}
                  <div>
                    <h3 className="font-medium">{framework.name}</h3>
                    <p className="text-sm text-muted-foreground">
                      Score: {framework.score}%
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <Badge className={getStatusColor(framework.status)}>
                    {framework.status.replace('-', ' ')}
                  </Badge>
                  <div className="w-24 mt-2">
                    <Progress value={framework.score} className="h-2" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Compliance Checks */}
      <Card>
        <CardHeader>
          <CardTitle>Compliance Checks</CardTitle>
          <CardDescription>
            Individual compliance requirement status
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {complianceChecks.map((check, index) => (
              <div key={index} className="flex items-center justify-between p-3 border rounded">
                <div className="flex items-center space-x-3">
                  {getStatusIcon(check.status)}
                  <div>
                    <span className="font-medium">{check.name}</span>
                    <Badge variant="outline" className="ml-2 text-xs">
                      {check.category}
                    </Badge>
                  </div>
                </div>
                <Badge className={getStatusColor(check.status)}>
                  {check.status}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Recent Reports */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Recent Compliance Reports
          </CardTitle>
          <CardDescription>
            Generated compliance reports and audit logs
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 border rounded">
              <div>
                <h4 className="font-medium">GDPR Compliance Report</h4>
                <p className="text-sm text-muted-foreground">Generated on Jan 15, 2024</p>
              </div>
              <Badge>Download</Badge>
            </div>
            <div className="flex items-center justify-between p-3 border rounded">
              <div>
                <h4 className="font-medium">SOC 2 Audit Results</h4>
                <p className="text-sm text-muted-foreground">Generated on Jan 10, 2024</p>
              </div>
              <Badge>Download</Badge>
            </div>
            <div className="flex items-center justify-between p-3 border rounded">
              <div>
                <h4 className="font-medium">Security Assessment</h4>
                <p className="text-sm text-muted-foreground">Generated on Jan 5, 2024</p>
              </div>
              <Badge>Download</Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}